import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { useAuth } from '../context/AuthContext';

const CreateCategoryModal = ({ show, onHide, mode, parentId, onCategoryCreated }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedParentId, setSelectedParentId] = useState(parentId || '');
    const [parentCategories, setParentCategories] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { encryptedApiCall, API_BASE_URL } = useAuth();

    useEffect(() => {
        if (show && mode === 'child') {
            fetchParentCategories();
        }
        if (parentId) {
            setSelectedParentId(parentId.toString());
        }
    }, [show, mode, parentId]);

    const fetchParentCategories = async () => {
        try {
            const data = await encryptedApiCall(`${API_BASE_URL}/categories?level=1`, {
                method: 'GET'
            });
            setParentCategories(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch parent categories');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!name.trim()) {
            setError('Category name is required');
            setLoading(false);
            return;
        }

        if (mode === 'child' && !selectedParentId) {
            setError('Please select a parent category');
            setLoading(false);
            return;
        }

        try {
            const body = {
                name: name.trim(),
                description: description.trim() || null
            };

            if (mode === 'child') {
                body.parent_id = parseInt(selectedParentId);
            }

            await encryptedApiCall(
                `${API_BASE_URL}/categories`,
                {
                    method: 'POST'
                },
                body
            );

            // Reset form
            setName('');
            setDescription('');
            setSelectedParentId(parentId ? parentId.toString() : '');
            onCategoryCreated();
        } catch (err) {
            setError(err.message || 'Failed to create category');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setName('');
        setDescription('');
        setSelectedParentId(parentId ? parentId.toString() : '');
        setError('');
        onHide();
    };

    const isParent = mode === 'parent';

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    Create {isParent ? 'Parent' : 'Child'} Category
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {!isParent && (
                        <Form.Group className="mb-3">
                            <Form.Label>Parent Category</Form.Label>
                            <Form.Select
                                value={selectedParentId}
                                onChange={(e) => setSelectedParentId(e.target.value)}
                                required
                                disabled={!!parentId} // Disable if parent is pre-selected
                            >
                                <option value="">Select parent category...</option>
                                {parentCategories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {parentId && (
                                <Form.Text className="text-muted">
                                    Parent category is pre-selected
                                </Form.Text>
                            )}
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Category Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={`Enter ${isParent ? 'parent' : 'child'} category name`}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            autoFocus
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description (Optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter category description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Creating...' : 'Create Category'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateCategoryModal;

